/**
 * AgentOsDemoNewsletter.jsx
 *
 * Public sandbox for the Newsletter Marketing Agent.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Sparkles, ArrowRight, Loader2, Copy, CheckCircle, Lock, Mail, Plus, X,
} from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const VOICES = [
  { id: "warm", name: "Vrúcny" }, { id: "formal", name: "Formálny" },
  { id: "friendly", name: "Priateľský" }, { id: "premium", name: "Premium" },
];
const LANGS = [
  { id: "sk", name: "Slovenčina" }, { id: "cs", name: "Čeština" },
  { id: "en", name: "English" }, { id: "de", name: "Deutsch" },
  { id: "pl", name: "Polski" }, { id: "hu", name: "Magyar" },
];
const SEGMENTS = [
  { id: "all_subscribers", name: "Všetci odberatelia" },
  { id: "vip", name: "VIP zákazníci" },
  { id: "lapsed", name: "Spiaci (3+ mes)" },
  { id: "new_signups", name: "Nový odberatelia" },
];

const EXAMPLES = [
  { brand_name: "ModaShop SK", season_or_theme: "Jarná kolekcia 2026", featured_products: [{ name: "Ľanové šaty Aurora" }, { name: "Plátenné tenisky Pure" }] },
  { brand_name: "TechHub", season_or_theme: "Back to school", featured_products: [{ name: "Notebook ProBook 14" }, { name: "Bezdrôtové slúchadlá X3" }, { name: "USB-C dock" }] },
  { brand_name: "ZdraváVýživa", season_or_theme: "Detox po sviatkoch", featured_products: [{ name: "Spirulina prášok" }, { name: "Detox čaj 30-dňový" }] },
];

export default function AgentOsDemoNewsletter() {
  const [form, setForm] = useState({
    brand_name: "", audience_segment: "all_subscribers",
    featured_products: [{ name: "" }],
    season_or_theme: "", brand_voice: "warm", lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });
  const [copied, setCopied] = useState(false);

  const loadExample = (i) => { setForm((f) => ({ ...f, ...EXAMPLES[i] })); setResult(null); };

  const addProduct = () => setForm((f) => ({ ...f, featured_products: [...f.featured_products, { name: "" }] }));
  const removeProduct = (i) => setForm((f) => ({ ...f, featured_products: f.featured_products.filter((_, idx) => idx !== i) }));
  const setProduct = (i, name) => setForm((f) => ({
    ...f, featured_products: f.featured_products.map((p, idx) => idx === i ? { ...p, name } : p),
  }));

  const submit = async (e) => {
    e.preventDefault();
    const filtered = form.featured_products.filter((p) => p.name.trim());
    if (!form.brand_name.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/newsletter`, {
        ...form, featured_products: filtered,
      });
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const copyAll = async () => {
    if (!result?.output) return;
    const o = result.output;
    const txt = `Subject: ${o.subject}\nPreview: ${o.preview}\n\n${(o.sections || []).map((s) => `## ${s.heading}\n${s.body}`).join("\n\n")}\n\n[CTA: ${o.cta_label} → ${o.cta_url_hint}]`;
    try { await navigator.clipboard.writeText(txt); setCopied(true); } catch {}
    setTimeout(() => setCopied(false), 2000);
  };

  const Left = (
    <form onSubmit={submit} className="space-y-4 p-6 bg-black border border-gray-800">
      <h2 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
        <Mail className="w-4 h-4" /> Newsletter brief
      </h2>

      <div className="flex flex-wrap gap-2">
        <span className="text-xs uppercase tracking-widest text-gray-500 self-center mr-2">Príklad:</span>
        {EXAMPLES.map((ex, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)}
            data-testid={`agent-os-demo-nl-example-${i}`}
            className="px-3 py-1 text-xs bg-[#0a0a0a] border border-gray-800 hover:border-emerald-700/60 text-gray-300 hover:text-emerald-300">
            {ex.brand_name}
          </button>
        ))}
      </div>

      <input required type="text" placeholder="Názov e-shopu *"
        value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
        data-testid="agent-os-demo-nl-brand"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <input type="text" placeholder='Téma / sezóna (napr. "Jarná kolekcia", "Black Friday")'
        value={form.season_or_theme} onChange={(e) => setForm({ ...form, season_or_theme: e.target.value })}
        data-testid="agent-os-demo-nl-theme"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <div>
        <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Highlight produkty</label>
        <div className="space-y-2" data-testid="agent-os-demo-nl-products">
          {form.featured_products.map((p, i) => (
            <div key={i} className="flex gap-2">
              <input type="text" placeholder={`Produkt #${i + 1}`}
                value={p.name} onChange={(e) => setProduct(i, e.target.value)}
                data-testid={`agent-os-demo-nl-product-${i}`}
                className="flex-1 px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />
              {form.featured_products.length > 1 && (
                <button type="button" onClick={() => removeProduct(i)}
                  data-testid={`agent-os-demo-nl-product-remove-${i}`}
                  className="px-2 border border-gray-800 hover:border-rose-500 text-gray-500 hover:text-rose-300">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={addProduct} data-testid="agent-os-demo-nl-add-product"
          className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
          <Plus className="w-3 h-3" /> Pridať produkt
        </button>
      </div>

      <div>
        <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Cieľová skupina</label>
        <select value={form.audience_segment} onChange={(e) => setForm({ ...form, audience_segment: e.target.value })}
          data-testid="agent-os-demo-nl-segment"
          className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {SEGMENTS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <select value={form.brand_voice} onChange={(e) => setForm({ ...form, brand_voice: e.target.value })}
          data-testid="agent-os-demo-nl-voice"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {VOICES.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}
          data-testid="agent-os-demo-nl-lang"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {LANGS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      <button type="submit" disabled={status.loading || !form.brand_name.trim()}
        data-testid="agent-os-demo-nl-submit"
        className="w-full px-5 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2">
        {status.loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Generujem…</>
          : <>Vygeneruj newsletter <Sparkles className="w-4 h-4" /></>}
      </button>
    </form>
  );

  const Right = (
    <div className="space-y-4">
      <div className="p-6 bg-gradient-to-br from-emerald-950/30 to-black border-2 border-emerald-700/40 min-h-[300px]">
        <h2 className="text-sm font-black uppercase tracking-widest text-emerald-300 mb-3">AI Newsletter</h2>

        {!result && !status.loading && !status.error && (
          <div className="text-center py-12 text-gray-500 text-sm" data-testid="agent-os-demo-nl-empty">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-gray-700" /> Vyplň brief a klikni „Vygeneruj"
          </div>
        )}
        {status.loading && (
          <div className="text-center py-12 text-emerald-300 text-sm" data-testid="agent-os-demo-nl-loading">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" /> AI píše newsletter…
          </div>
        )}
        {status.error && (
          <div className="p-4 bg-rose-950/30 border border-rose-700/40 text-rose-200 text-sm" data-testid="agent-os-demo-nl-error">
            <div className="font-bold mb-1">{status.error?.message || "Niečo zlyhalo"}</div>
            {status.error?.cta_url && (
              <a href={status.error.cta_url} className="inline-flex items-center gap-1 mt-2 text-emerald-300 underline text-xs font-bold">
                Registruj sa zadarmo <ArrowRight className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {result?.output && (
          <div data-testid="agent-os-demo-nl-result" className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Subject</div>
              <div className="p-3 bg-black/50 border border-emerald-900/40 text-white font-bold">{result.output.subject}</div>
              <div className="text-[10px] text-gray-500 italic mt-1">Preview: {result.output.preview}</div>
            </div>
            {(result.output.sections || []).map((s, i) => (
              <div key={i} data-testid={`agent-os-demo-nl-section-${i}`}>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Sekcia {i + 1}</div>
                <div className="p-3 bg-black/50 border border-emerald-900/40 text-white">
                  <div className="font-bold mb-1">{s.heading}</div>
                  <div className="text-sm whitespace-pre-line leading-relaxed">{s.body}</div>
                </div>
              </div>
            ))}
            <div className="text-center">
              <span className="inline-block px-5 py-2 bg-emerald-400 text-black font-black text-xs uppercase tracking-widest">{result.output.cta_label}</span>
              <div className="text-[10px] text-gray-500 mt-1">Cieľ: {result.output.cta_url_hint}</div>
            </div>
            <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
              <span className="px-2 py-1 bg-gray-900 text-gray-400">{result.latency_ms} ms</span>
              {result.used_fallback && <span className="px-2 py-1 bg-amber-900/30 text-amber-300 font-bold">Fallback</span>}
              <span className="px-2 py-1 bg-gray-900 text-gray-400">Zostáva dnes: {result.remaining_today}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={copyAll} data-testid="agent-os-demo-nl-copy"
                className="flex-1 px-4 py-2 border border-gray-700 hover:border-emerald-400 text-xs font-bold uppercase tracking-widest text-white flex items-center justify-center gap-2">
                {copied ? <><CheckCircle className="w-3 h-3 text-emerald-400" /> Skopírované</> : <><Copy className="w-3 h-3" /> Kopírovať</>}
              </button>
              <a href="/agent-os/onboard" data-testid="agent-os-demo-nl-cta"
                className="flex-1 px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2">
                Aktivovať <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-black border border-gray-800 text-xs text-gray-500 flex gap-3 items-start">
        <Lock className="w-4 h-4 shrink-0 mt-0.5 text-gray-600" />
        <div>Demo má limit 3 generácie / IP / 24h. <a href="/privacy" className="text-emerald-400 underline ml-1">Privacy →</a></div>
      </div>
    </div>
  );

  return (
    <AgentOsDemoShell
      testid="agent-os-demo-newsletter-page"
      h1={<>Newsletter <span className="text-emerald-400">Marketing</span></>}
      lead="Brand + pár produktov → AI vyskladá kompletný týždenný newsletter so subjectom, 2-3 sekciami a CTA. 3 pokusy zadarmo."
      left={Left}
      right={Right}
    />
  );
}
